import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import PageTransition from '../components/PageTransition';
import Toast, { ToastType } from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import AdditionalNotesModal from '../components/AdditionalNotesModal';
import { getOrderDetails, getOrderInfo, deleteOrder } from '../services/sparePartsService';
import { getNotesData } from '../services/api';
import { IslemAdi } from '../constants/status';
import ProductImage from '../components/ProductImage';
import { useMenu } from '../contexts/MenuContext';
import { formatPhoneNumber, isValidTurkishMobile } from '../utils/phoneUtils';
import { notifyAccountCacheDelete } from '../utils/accountCacheUtils';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  const queryClient = useQueryClient();
  
  // State değişkenleri
  const [orderItems, setOrderItems] = useState<any[]>([]);
  const [orderNo, setOrderNo] = useState<string>('');
  const [orderInfo, setOrderInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // Scroll yönünü takip etmek için state ve ref
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showBackButton, setShowBackButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);
  
  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' as ToastType });
  
  // Silme onay dialogu state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Ek notlar state (Notlar store'undan)
  const [additionalNotes, setAdditionalNotes] = useState<any[]>([]);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  // Toast functions
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };

  // HTML'den temiz metin çıkarma ve boş olup olmadığını kontrol etme
  const hasVisibleText = (htmlString: string | null | undefined): boolean => {
    if (!htmlString) return false;
    
    // HTML taglarını kaldır
    const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
    
    // Boş string, sadece whitespace veya &nbsp; gibi HTML entity'leri kontrol et
    return textContent.length > 0 && textContent !== '&nbsp;';
  };

  // Ek notların varlığını kontrol etme (component'e geçirmek için)
  const hasAdditionalNotes = additionalNotes && additionalNotes.length > 0 && 
    additionalNotes.some(note => note.Notlar && hasVisibleText(note.Notlar));

  // Sipariş silme fonksiyonları
  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!orderInfo?.Id) return;
    
    try {
      setIsDeleting(true);
      const response = await deleteOrder(orderInfo.Id);
      
      if (response.success) {
        showToast('Sipariş başarıyla silindi.', 'success');
        
        // Account cache'i güncelle
        notifyAccountCacheDelete('order');
        
        // React Query cache'ini invalidate et - tüm orders sorgularını yenile
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        
        setTimeout(() => {
          navigate('/orders');
        }, 2000);
      } else {
        showToast('Sipariş silinirken bir hata oluştu.', 'error');
      }
    } catch (error) {
      console.error('Sipariş silme hatası:', error);
      showToast('Sipariş silinirken bir hata oluştu.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
  };

  // Geri gitme fonksiyonu
  const handleGoBack = () => {
    navigate(-1); // Bir önceki sayfaya git
  };

  // Siparişin silinebilir olup olmadığını kontrol et
  const canDeleteOrder = orderInfo && orderInfo.IslemAdi === IslemAdi.ONAY_BEKLIYOR;

  // Sipariş detaylarını yükle
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await getOrderDetails(parseInt(id));
        
        if (response.data && response.data.length > 0) {
          setOrderItems(response.data);
          const orderNumber = response.data[0].SiparisNo;
          setOrderNo(orderNumber);
          
          // Toplam tutarı hesapla
          const total = response.data.reduce((sum: number, item: any) => sum + item.SiparisTutari, 0);
          setTotalAmount(total);
          
          // Sipariş bilgilerini yükle
          if (orderNumber) {
            const orderInfoResponse = await getOrderInfo(orderNumber);
            if (orderInfoResponse.data && orderInfoResponse.data.length > 0) {
              const orderData = orderInfoResponse.data[0];
              setOrderInfo(orderData);

              // Eğer sipariş durumu "Onay Bekliyor" değilse ek notları da çek
              if (orderData.IslemAdi !== IslemAdi.ONAY_BEKLIYOR) {
                try {
                  const additionalNotesResponse = await getNotesData('Notlar', 'Siparisler', id);
                  if (additionalNotesResponse.success && additionalNotesResponse.data) {
                    setAdditionalNotes(additionalNotesResponse.data);
                  }
                } catch (additionalNotesError) {
                  console.error('Ek notlar yüklenirken hata:', additionalNotesError);
                  // Ek notların yüklenememesi ana işlemi engellemez
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Sipariş detayları yüklenirken hata:', error);
        showToast('Sipariş detayları yüklenirken bir hata oluştu.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    if (!loading && orderInfo) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500); // 500ms gecikme
      
      return () => clearTimeout(timer);
    }
  }, [loading, orderInfo]);

  // Scroll olayını dinle - butonu gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonları gizle
      if (isModalOpen) {
        setShowDeleteButton(false);
        setShowBackButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonların durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowDeleteButton(true);
        setShowBackButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Butonlar için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowDeleteButton(false);
        setShowBackButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowDeleteButton(true);
        setShowBackButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonları tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowDeleteButton(true);
        setShowBackButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonları gizle
        setShowDeleteButton(false);
        setShowBackButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);



  if (loading) {
    return (
      <div className="max-w-4xl mx-auto min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Sipariş Detayları
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Siparişinizin detaylı bilgileri
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating Geri Butonu - Sadece mobilde görünür */}
          <AnimatePresence>
            {!loading && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-4 right-4 z-50 md:hidden"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.4
                }}
              >
                <motion.button
                  onClick={handleGoBack}
                  className="relative border border-gray-200 dark:border-gray-700 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-700 dark:hover:to-gray-600 text-gray-700 dark:text-gray-300 shadow-lg shadow-gray-500/25 dark:shadow-black/25 hover:shadow-xl hover:shadow-gray-500/30 dark:hover:shadow-black/35 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 dark:focus:ring-gray-500 dark:focus:ring-offset-gray-800"
                  aria-label="Geri Git"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="rounded-full p-2.5 border border-white dark:border-gray-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </div>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating Silme Butonu - Mobilde geri butonunun altında, desktop'ta en üstte */}
          <AnimatePresence>
            {canDeleteOrder && !loading && !isInitialLoad && showDeleteButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-16 md:top-4 right-4 z-50"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  delay: 0.1
                }}
              >
                <motion.button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2.5 rounded-full shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isDeleting ? 'Siliniyor...' : 'Siparişi Sil'}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>

                  {isDeleting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-600 rounded-full">
                      <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content Card - Sadece sipariş bulunduğunda göster */}
        {!(orderItems.length === 0 && !orderInfo) && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
              <div className="max-w-md mx-auto">
                {/* Header Icon */}
                <div className="flex justify-center mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>

                {/* Sipariş No ve Durum */}
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    Sipariş No: {orderNo}
                  </h2>
                  
                  {orderInfo && (
                    <div className="space-y-3">
                      {/* Sipariş Durumu */}
                      <div className="flex justify-center">
                        {orderInfo.IslemAdi !== IslemAdi.ONAY_BEKLIYOR && hasAdditionalNotes ? (
                          // Ek notlar varsa tüm etiketi tıklanabilir yap
                          <button
                            onClick={() => setIsNotesModalOpen(true)}
                            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 hover:shadow-md ${
                              orderInfo.IslemAdi === IslemAdi.ONAYLANDI
                                ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300 hover:from-green-200 hover:via-emerald-200 hover:to-green-300 dark:hover:from-green-900/40 dark:hover:via-green-900/35 dark:hover:to-green-800/40'
                                : orderInfo.IslemAdi === IslemAdi.IPTAL_EDILDI
                                ? 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300 hover:from-red-200 hover:via-rose-200 hover:to-red-300 dark:hover:from-red-900/40 dark:hover:via-red-900/35 dark:hover:to-red-800/40'
                                : 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300 hover:from-blue-200 hover:via-indigo-200 hover:to-blue-300 dark:hover:from-blue-900/40 dark:hover:via-blue-900/35 dark:hover:to-blue-800/40'
                            }`}
                          >
                            {orderInfo.IslemAdi}
                            <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v3a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                            </svg>
                          </button>
                        ) : (
                          // Ek notlar yoksa normal etiket
                          <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                            orderInfo.IslemAdi === IslemAdi.ONAY_BEKLIYOR 
                              ? 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300'
                              : orderInfo.IslemAdi === IslemAdi.ONAYLANDI
                              ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300'
                              : orderInfo.IslemAdi === IslemAdi.IPTAL_EDILDI
                              ? 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300'
                              : 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300'
                          }`}>
                            {orderInfo.IslemAdi}
                          </span>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Son Etkinlik Bilgisi */}
        {orderInfo?.SonEtkinlik && (
          <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 dark:from-amber-900/20 dark:via-amber-900/15 dark:to-amber-800/20 border border-amber-200 dark:border-amber-800 rounded-lg mt-6">
            <div className="rounded-lg p-3 border border-white dark:border-gray-700">
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700">
                  <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium tracking-wide text-amber-700 dark:text-amber-300">SON ETKİNLİK</p>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{orderInfo.SonEtkinlik}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sipariş Öğeleri */}
        {orderItems.length > 0 ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sipariş Edilen Ürünler ({orderItems.length} ürün)
            </h3>
            
            <div className="space-y-4">
              {orderItems.map((item, index) => (
                <motion.div
                  key={item.Id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600"
                >
                  <div className="rounded-lg p-4 border border-white dark:border-gray-700 flex items-start space-x-4">
                  {/* Ürün Görseli */}
                  <div className="flex-shrink-0">
                    <ProductImage
                      imageId={item.Gorsel}
                      alt={item.Ad}
                      size="w-16 h-16"
                      iconSize="w-8 h-8"
                    />
                  </div>

                  {/* Ürün Bilgileri */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <div className="mb-2 sm:mb-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white break-words">
                          {item.Ad}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                          {item.Kod}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                          {item.GrupAdi}
                        </p>
                        {item.PromosyonluMu && (
                          <span className="inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                            Promosyon
                          </span>
                        )}
                      </div>

                      {/* Fiyat ve Miktar */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          ₺{item.SiparisTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ₺{item.Fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} x {item.Miktar}
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Toplam Tutar */}
            <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-6">
              <div className="flex justify-between items-center">
                <div className="text-lg font-medium text-gray-900 dark:text-white">
                  Toplam Tutar:
                </div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ₺{totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            </div>
          </div>
        ) : !orderInfo ? (
          // Hem orderItems hem orderInfo boş ise "Sipariş bulunamadı"
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <div className="max-w-md mx-auto">
              {/* Header Icon - Sipariş bulunamadı durumunda da simge gösterilsin */}
              <div className="flex justify-center mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-900/30 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              {/* Hata mesajı */}
              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  Sipariş Bulunamadı
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Aradığınız sipariş bulunamadı. Lütfen sipariş numaranızı kontrol edin.
                </p>
              </div>

              {/* Ana Sayfaya Dön düğmesi - Modern blue/indigo gradyan */}
              <button
                onClick={() => navigate('/')}
                className={`
                  w-full flex items-center justify-center px-6 py-3.5
                  text-base font-semibold text-white
                  bg-gradient-to-r from-blue-600 to-indigo-600
                  hover:from-blue-700 hover:to-indigo-700
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  rounded-lg shadow-lg shadow-blue-500/25
                  transform transition-all duration-200
                  hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
                  active:scale-[0.98]
                `}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Ana Sayfaya Dön
              </button>
            </div>
            </div>
          </div>
        ) : (
          // Sadece orderItems boş ise mevcut mesaj
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">
                Bu siparişe ait ürün bulunamadı.
              </p>
            </div>
            </div>
          </div>
        )}

        {/* Sipariş Bilgileri */}
        {orderInfo && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Sipariş Bilgileri
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Teslimat Adresi */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2 mb-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    Teslimat Adresi
                  </h4>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600 md:border-transparent">
                  <div className="text-gray-900 dark:text-white font-medium leading-relaxed text-sm break-words">
                    {orderInfo.Adres}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-500">
                      {orderInfo.IlceAdi}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-500">
                      {orderInfo.IlAdi}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-500">
                      {orderInfo.UlkeAdi}
                    </span>
                  </div>
                  
                  {orderInfo.PostaKodu && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Posta Kodu:</span> {orderInfo.PostaKodu}
                    </div>
                  )}
                  
                  {orderInfo.TasiyiciAdi && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                        </svg>
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Taşıyıcı Firma:</span>
                        <span className="text-sm font-semibold text-blue-900 dark:text-blue-100 break-words">{orderInfo.TasiyiciAdi}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ayraç */}
              <div className="border-t border-gray-200 dark:border-gray-600 mt-2 lg:hidden"></div>

              {/* İletişim ve Notlar */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2 mb-3">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                    İletişim
                  </h4>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-200 dark:border-gray-600 md:border-transparent">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-900 dark:text-white font-medium break-all">
                        {isValidTurkishMobile(orderInfo.Telefon1) 
                          ? formatPhoneNumber(orderInfo.Telefon1)
                          : orderInfo.Telefon1
                        }
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700">
                        Birincil
                      </span>
                    </div>
                    
                    {orderInfo.Telefon2 && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600 dark:text-gray-400 break-all">
                          {isValidTurkishMobile(orderInfo.Telefon2) 
                            ? formatPhoneNumber(orderInfo.Telefon2)
                            : orderInfo.Telefon2
                          }
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-600 dark:via-gray-600/90 dark:to-gray-500 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-500">
                          İkincil
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Notlar */}
                  {(hasVisibleText(orderInfo.ServisNotlari) || hasVisibleText(orderInfo.Notlar)) && (
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                      <div className="space-y-3">
                        {hasVisibleText(orderInfo.ServisNotlari) && (
                          <div className="bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 border-l-4 border-blue-400 p-3 rounded border border-indigo-100 dark:border-gray-600">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-4 w-4 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v3a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                  Sipariş Notları
                                </p>
                                <div 
                                  className="text-sm text-blue-700 dark:text-blue-300"
                                  dangerouslySetInnerHTML={{ __html: orderInfo.ServisNotlari }}
                                />
                              </div>
                            </div>
                          </div>
                        )}


                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            </div>
          </div>
        )}
      </div>

      {/* Silme Onay Dialogu */}
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        title="Siparişi Sil"
        message="Bu siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        autoClose={true}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Ek Notlar Modal */}
      <AdditionalNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        notes={additionalNotes}
        title="Onay Ayrıntısı"
      />

      {/* Toast */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </PageTransition>
  );
} 